<script setup>
import { ref } from 'vue'
import { useForm } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import TurnstileWidget from '@/Components/Public/TurnstileWidget.vue'

// Contact block + submit for the on-site "Request to book". The parent card
// owns dates/guests state and passes them via `details`; this component owns
// name/contact/note and the POST. Public-page styling (plan-card idiom).
const props = defineProps({
  bookableType: { type: String, required: true },   // 'tour' | 'vehicle' | 'property'
  bookableId:   { type: [Number, String], required: true },
  details:      { type: Object, required: true },   // { start_on, end_on?, adults?, children? }
  disabled:     { type: Boolean, default: false },  // parent gates on missing dates
  showDelivery: { type: Boolean, default: false },  // show delivery address field
})

const { t } = useI18n()
// Required since 2026-10-23: the budget band qualifies the lead before anyone replies.
const BUDGET_BANDS = ['under_30k', '30_60k', '60_120k', '120k_plus']
const form = useForm({ name: '', phone: '', email: '', note: '', delivery_address: '', budget_band: '', website: '', turnstile_token: '' })

const turnstile = ref(null)

const base = () => (window.location.pathname.startsWith('/_p') ? '/_p' : '')

function submit() {
  form.transform(d => ({
    booking_request: {
      bookable_type: props.bookableType,
      bookable_id:   props.bookableId,
      ...props.details,
      name: d.name, phone: d.phone, email: d.email, note: d.note, delivery_address: d.delivery_address,
      budget_band: d.budget_band,
    },
    website: d.website,
    turnstile_token: d.turnstile_token,
  })).post(`${base()}/booking_requests`, {
    preserveScroll: true,
    onFinish: () => turnstile.value?.reset()
  })
}
</script>

<template>
  <form class="brf" @submit.prevent="submit">
    <input v-model="form.website" type="text" name="website" tabindex="-1" autocomplete="off" class="brf__honeypot" aria-hidden="true">
    <label class="brf__field">
      <span class="brf__label">{{ t('public.booking_request.name') }}</span>
      <input v-model="form.name" type="text" class="brf__input" required maxlength="80" autocomplete="name">
    </label>
    <div class="brf__grid">
      <label class="brf__field">
        <span class="brf__label">{{ t('public.booking_request.phone') }}</span>
        <input v-model="form.phone" type="tel" class="brf__input" autocomplete="tel">
      </label>
      <label class="brf__field">
        <span class="brf__label">{{ t('public.booking_request.email') }}</span>
        <input v-model="form.email" type="email" class="brf__input" autocomplete="email">
      </label>
    </div>
    <p class="brf__hint">{{ t('public.booking_request.contact_hint') }}</p>
    <label v-if="showDelivery" class="brf__field">
      <span class="brf__label">{{ t('public.booking_request.delivery_address') }}</span>
      <input v-model="form.delivery_address" type="text" class="brf__input" maxlength="200"
             :placeholder="t('public.booking_request.delivery_placeholder')" autocomplete="street-address">
    </label>
    <label class="brf__field">
      <span class="brf__label">{{ t('public.plan.budget') }}</span>
      <select v-model="form.budget_band" class="brf__input" required>
        <option value="" disabled>{{ t('public.plan.budget_pick') }}</option>
        <option v-for="b in BUDGET_BANDS" :key="b" :value="b">{{ t(`public.plan.budgets.${b}`) }}</option>
      </select>
    </label>
    <label class="brf__field">
      <span class="brf__label">{{ t('public.booking_request.note') }}</span>
      <textarea v-model="form.note" class="brf__input brf__textarea" rows="2" maxlength="2000" />
    </label>
    <TurnstileWidget ref="turnstile" v-model="form.turnstile_token" />
    <button type="submit" class="brf__submit" :disabled="form.processing || disabled">
      {{ t('public.booking_request.submit') }}
    </button>
    <p v-if="disabled" class="brf__hint">{{ t('public.booking_request.pick_date') }}</p>
  </form>
</template>

<style scoped>
.brf { display: grid; gap: 0.6rem; margin-top: 0.9rem; padding-top: 0.9rem; border-top: 1px solid var(--line); }
.brf__honeypot { position: absolute; left: -9999px; }
.brf__grid { display: grid; grid-template-columns: 1fr 1fr; gap: 0.6rem; }
.brf__field { display: block; }
.brf__label { display: block; font-size: 0.8rem; font-weight: 600; color: var(--inkSoft); margin-bottom: 0.2rem; }
.brf__input {
  width: 100%; padding: 0.5rem 0.7rem; font-size: 0.95rem; color: var(--ink);
  background: var(--surface); border: 1px solid var(--line); border-radius: 0.5rem;
}
.brf__textarea { resize: vertical; }
.brf__hint { margin: 0; font-size: 0.78rem; color: var(--muted); }
/* Mirrors Components/Public/LuxButton (light tone, solid) — kept as plain
   CSS here because the submit must stay a real <button> inside the form. */
.brf__submit {
  display: inline-flex; align-items: center; justify-content: center; width: 100%;
  padding: 0.95rem 1.6rem; border-radius: 999px; cursor: pointer; appearance: none;
  font-family: var(--font-body, var(--body)); font-size: 0.78rem; font-weight: 600;
  letter-spacing: 0.14em; text-transform: uppercase; line-height: 1;
  background: var(--night, var(--ink)); color: var(--foam, #ECF3F6); border: 1px solid var(--night, var(--ink));
  box-shadow: 0 16px 34px -20px color-mix(in oklch, var(--ink) 60%, transparent), inset 0 0 0 1px color-mix(in oklch, var(--gold, #D8B46B) 40%, transparent);
  transition: transform 160ms ease, background 160ms ease;
}
.brf__submit:hover:not(:disabled) { background: var(--night2, var(--ink)); border-color: var(--night2, var(--ink)); transform: translateY(-1px); }
.brf__submit:disabled { opacity: 0.5; cursor: not-allowed; }
</style>
